/**
 * Edit a Care Concierge case (Feature 106).
 *
 * One notes field with an explicit share switch rather than an internal/external pair. That makes
 * the switch the only thing between a candid care note and the client's advisor, so it is stated
 * in full next to the field instead of sitting as a quiet checkbox at the bottom.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { CarePlanField } from './CarePlanField';
import { CaseFollowUps } from './CaseFollowUps';
import { ReferredProvidersField } from './ReferredProvidersField';
import type {
  CaseFollowUp,
  CaseRequestSource,
  CaseStatus,
  ConciergeCase,
  ReferredProvider,
  UpdateCasePayload,
} from '../../services/careConciergeCasesApi';

const STATUSES: Array<{ value: CaseStatus; label: string }> = [
  { value: 'AWAITING_CLIENT', label: 'Awaiting client' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'OCCURRED', label: 'Occurred' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'NOT_COMPLETED', label: 'Not completed' },
];

const SOURCES: Array<{ value: CaseRequestSource; label: string }> = [
  { value: 'PAID_CONSULTATION', label: 'Paid concierge consultation' },
  { value: 'SP_NOT_FOUND', label: 'SP-not-found request' },
  { value: 'DIRECT_ADVISOR_REQUEST', label: 'Direct advisor request' },
  { value: 'DISCOVERY_CALL', label: 'Discovery call' },
];

/**
 * Inserted into the note rather than shown as placeholder text, so what the team sees is what
 * gets saved — a prompt that vanishes on the first keystroke teaches nothing.
 */
const NOTE_TEMPLATE = [
  'Conversation summary:',
  '',
  'Family situation:',
  '',
  'Next steps:',
  '',
  'Follow-up date:',
  '',
].join('\n');

const toDateInput = (iso: string | null): string => (iso ? iso.slice(0, 10) : '');
const toDateTimeInput = (iso: string | null): string => (iso ? iso.slice(0, 16) : '');

type CaseEditorDialogProps = {
  conciergeCase: ConciergeCase;
  isSaving: boolean;
  onSave: (payload: UpdateCasePayload) => void;
  /** Called after a care-plan action, which saves outside this form, with the new state. */
  onCarePlanChanged: (hasCarePlan: boolean) => void;
  /** Same for follow-ups: they are rows, saved as they are added rather than held until Save. */
  onFollowUpsChanged: (followUps: CaseFollowUp[]) => void;
  onClose: () => void;
};

export const CaseEditorDialog = ({
  conciergeCase,
  isSaving,
  onSave,
  onCarePlanChanged,
  onFollowUpsChanged,
  onClose,
}: CaseEditorDialogProps) => {
  const [status, setStatus] = useState<CaseStatus>(conciergeCase.status);
  const [requestSource, setRequestSource] = useState<CaseRequestSource>(
    conciergeCase.requestSource,
  );
  const [scheduledAt, setScheduledAt] = useState(toDateTimeInput(conciergeCase.scheduledAt));
  const [occurredAt, setOccurredAt] = useState(toDateInput(conciergeCase.occurredAt));
  const [recommendationsAt, setRecommendationsAt] = useState(
    toDateInput(conciergeCase.recommendationsProvidedAt),
  );
  const [notes, setNotes] = useState(conciergeCase.notes ?? '');
  const [shared, setShared] = useState(conciergeCase.sharedWithAdvisor);
  const [providers, setProviders] = useState<ReferredProvider[]>(conciergeCase.referredProviders);
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    if (status === 'SCHEDULED' && !scheduledAt) {
      setError('A scheduled consultation needs its booked date and time.');
      return;
    }

    onSave({
      status,
      requestSource,
      ...(scheduledAt ? { scheduledAt: new Date(scheduledAt).toISOString() } : {}),
      ...(occurredAt ? { occurredAt: new Date(occurredAt).toISOString() } : {}),
      ...(recommendationsAt
        ? { recommendationsProvidedAt: new Date(recommendationsAt).toISOString() }
        : {}),
      notes: notes.trim(),
      sharedWithAdvisor: shared,
      // Either a link to a bQuest provider or a name, never both — the column's CHECK rejects a
      // row carrying the two, and the API says so before the database has to.
      referredProviders: providers.map((entry) =>
        entry.serviceProviderId
          ? { serviceProviderId: entry.serviceProviderId }
          : { freeTextName: entry.name },
      ),
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit case</DialogTitle>
          <DialogDescription>
            {conciergeCase.personName || conciergeCase.personEmail}
            {conciergeCase.advisorName && ` · ${conciergeCase.advisorName}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="cc-case-status">Status</Label>
              <select
                id="cc-case-status"
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value as CaseStatus);
                  setError(null);
                }}
                disabled={isSaving}
              >
                {STATUSES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="cc-case-source">Request source</Label>
              <select
                id="cc-case-source"
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={requestSource}
                onChange={(event) => setRequestSource(event.target.value as CaseRequestSource)}
                disabled={isSaving}
              >
                {SOURCES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {status === 'NOT_COMPLETED' && (
            <p className="rounded-md bg-muted p-2 text-xs text-muted-foreground">
              Saving this returns the sponsored session to the pool it came from.
            </p>
          )}

          {/* On its own row: datetime-local renders a date AND a time field plus the picker
              indicator, and in a half-width column Chrome runs out of room and clips the
              indicator — leaving a field that looks unclickable next to a date field that has an
              icon. */}
          <div>
            <Label htmlFor="cc-case-scheduled">Booked date and time</Label>
            <Input
              id="cc-case-scheduled"
              type="datetime-local"
              value={scheduledAt}
              onChange={(event) => {
                setScheduledAt(event.target.value);
                setError(null);
              }}
              disabled={isSaving}
            />
          </div>

          {/* items-end so the two inputs sit on the same line: the labels are different lengths and
              one wraps at this width, which would otherwise push its field down alone. */}
          <div className="grid items-end gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="cc-case-occurred">Consultation date</Label>
              <Input
                id="cc-case-occurred"
                type="date"
                value={occurredAt}
                onChange={(event) => setOccurredAt(event.target.value)}
                disabled={isSaving}
              />
            </div>
            <div>
              <Label htmlFor="cc-case-recommendations">Recommendations provided</Label>
              <Input
                id="cc-case-recommendations"
                type="date"
                value={recommendationsAt}
                onChange={(event) => setRecommendationsAt(event.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>

          <ReferredProvidersField value={providers} onChange={setProviders} disabled={isSaving} />

          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="cc-case-notes">Notes</Label>
              {!notes && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setNotes(NOTE_TEMPLATE)}
                  disabled={isSaving}
                >
                  Insert template
                </Button>
              )}
            </div>
            <Textarea
              id="cc-case-notes"
              rows={8}
              maxLength={5000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={isSaving}
            />
          </div>

          <CaseFollowUps
            caseId={conciergeCase.id}
            followUps={conciergeCase.followUps}
            onChanged={onFollowUpsChanged}
          />

          <CarePlanField
            caseId={conciergeCase.id}
            hasCarePlan={conciergeCase.hasCarePlan}
            sharedWithAdvisor={shared}
            onChanged={onCarePlanChanged}
          />

          <label
            className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
              shared ? 'border-amber-400 bg-amber-50' : 'border-border'
            }`}
          >
            <input
              type="checkbox"
              className="mt-0.5"
              checked={shared}
              onChange={(event) => setShared(event.target.checked)}
              disabled={isSaving}
            />
            <span>
              <span className="font-medium">Share this case with the advisor</span>
              <span className="block text-xs text-muted-foreground">
                {shared
                  ? 'The advisor will see everything written above, and the care plan, on their dashboard.'
                  : 'Off: these notes and the care plan stay on this screen.'}
              </span>
            </span>
          </label>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save case'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
